import { useEffect, useRef, useState } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  Menu,
  X,
  LogOut,
  UserCircle2,
  LayoutDashboard,
  Building2,
  Network,
  Layers,
} from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../../lib/auth/AuthContext.jsx'

const NAV_AUTHORING = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/organizations', label: 'Organizations', icon: Network },
  { to: '/spaces', label: 'Spaces', icon: Building2 },
  { to: '/assets', label: 'Assets', icon: Layers },
]

function deriveProfile(user) {
  if (!user) return { initials: '··', name: '—', role: '', email: '' }
  const email = user.email || ''
  const namePart = email.split('@')[0] || 'User'
  const initials = namePart
    .split(/[._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || namePart.slice(0, 2).toUpperCase()
  const roleLabel = (user.role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return { initials, name: namePart, role: roleLabel || 'Member', email }
}

export default function AppShell() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const profile = deriveProfile(user)

  const [menuOpen, setMenuOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const menuRef = useRef(null)
  const profileWrapRef = useRef(null)

  useEffect(() => {
    setMenuOpen(false)
    setProfileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false) }
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!profileOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setProfileOpen(false) }
    const onClick = (e) => {
      if (profileWrapRef.current && !profileWrapRef.current.contains(e.target)) {
        setProfileOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [profileOpen])

  const handleLogout = async () => {
    setProfileOpen(false)
    setMenuOpen(false)
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <div className="app-topbar__inner">
          <div className="brand">
            <img className="brand__mark" src="/arrow-icon.svg" alt="" width={30} height={30} />
            <span className="brand__name">ARrow</span>
          </div>

          <nav className="nav-tabs" aria-label="Primary">
            {NAV_AUTHORING.map((item) => (
              <NavTab key={item.to} {...item} />
            ))}
          </nav>

          <div className="app-topbar__actions">
            <div className="profile-wrap" ref={profileWrapRef}>
              <button
                type="button"
                className={clsx('profile', profileOpen && 'is-open')}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((v) => !v)}
              >
                <span className="profile__avatar">{profile.initials}</span>
                <span className="profile__meta">
                  <span className="profile__name">{profile.name}</span>
                  <span className="profile__role">{profile.role}</span>
                </span>
                <ChevronDown
                  size={14}
                  color="var(--text-muted)"
                  className={clsx('profile__chevron', profileOpen && 'is-open')}
                />
              </button>

              {profileOpen && (
                <div className="profile-menu" role="menu">
                  <div className="profile-menu__header">
                    <span className="profile-menu__avatar">{profile.initials}</span>
                    <div>
                      <div className="profile-menu__name">{profile.name}</div>
                      <div className="profile-menu__email">{profile.email}</div>
                    </div>
                  </div>
                  <div className="profile-menu__divider" />
                  <button
                    type="button"
                    className="profile-menu__item"
                    role="menuitem"
                    onClick={() => { setProfileOpen(false); navigate('/settings') }}
                  >
                    <UserCircle2 size={16} />
                    <span>Account settings</span>
                  </button>
                  <button
                    type="button"
                    className="profile-menu__item profile-menu__item--danger"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    <span>Log out</span>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className={clsx('icon-btn icon-btn--menu', menuOpen && 'is-active')}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div id="mobile-nav" className="mobile-nav" ref={menuRef} role="dialog" aria-label="Navigation">
            <div className="mobile-nav__group-label">Authoring</div>
            {NAV_AUTHORING.map((item) => (
              <MobileNavLink key={item.to} {...item} />
            ))}
            <div className="mobile-nav__divider" />
            <button
              type="button"
              className="mobile-nav__link mobile-nav__link--danger"
              onClick={handleLogout}
            >
              <LogOut aria-hidden />
              <span>Log out</span>
            </button>
          </div>
        )}
      </header>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  )
}

function NavTab({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      end={to === '/dashboard'}
      className={({ isActive }) => clsx('nav-tab', isActive && 'is-active')}
      title={label}
    >
      {Icon && <Icon className="nav-tab__icon" aria-hidden />}
      <span className="nav-tab__label">{label}</span>
    </NavLink>
  )
}

function MobileNavLink({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      end={to === '/dashboard'}
      className={({ isActive }) => clsx('mobile-nav__link', isActive && 'is-active')}
    >
      {Icon && <Icon aria-hidden />}
      <span>{label}</span>
    </NavLink>
  )
}
